import {
  Box,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AutoAwesome as SparklesIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useTranslationApp } from "../../../lib/i18next";
import { useSelectorApp } from "../../../lib/redux";
import {
  MODAL_CREATE_WITH_IA_CONFIGURATION_ADVANCED,
  MODAL_CREATE_WITH_IA_CONFIGURATION_BASIC,
  MODAL_CREATE_WITH_IA_CONFIGURATION_INSTRUCTIONS,
  MODAL_CREATE_WITH_IA_CONFIGURATION_THEME,
  MODAL_CREATE_WITH_IA_PROMPT_OPTIONAL,
  MODAL_CREATE_WITH_IA_PROMPT_THEME_OPTIONAL,
  MODAL_CREATE_WITH_IA_GENERATE_WITH_AI,
  MODAL_CREATE_WITH_IA_GENERATIONS_IN_PROGRESS,
  MODAL_CREATE_WITH_IA_LABEL_LANGUAGE,
  MODAL_CREATE_WITH_IA_LABEL_ACCENT,
  MODAL_CREATE_WITH_IA_LABEL_LOCATION,
  MODAL_CREATE_WITH_IA_LABEL_MEDIA_TYPE,
  MODAL_CREATE_WITH_IA_LABEL_VIDEO_TYPE,
  MODAL_CREATE_WITH_IA_LABEL_PLATFORM,
  MODAL_CREATE_WITH_IA_SUBTITLE,
  MODAL_CREATE_WITH_IA_TITLE,
  TEXT_IN_IMAGE,
  VIDEO_LANGUAGE,
  LOGO_IN_IMAGE,
  FIELD_NUMBER_GREATER_THAN,
  FIELD_REQUIRED,
  FIELD_NUMBER_0_OR_NEGATIVE,
  ASPECT_RATIO_PLACEHOLDER,
  CREATIVES_REMAINING_SHORT,
  CREATIVES_REMAINING_SHORT_SINGULAR,
  VIDEOS_REMAINING_SHORT,
  VIDEOS_REMAINING_SHORT_SINGULAR,
  MODAL_CREATE_WITH_IA_LABEL_WITH_SUBTITLES,
  MODAL_CREATE_WITH_IA_LABEL_AI_WATERMARK,
  MODAL_CREATE_WITH_IA_AI_WATERMARK_DESCRIPTION,
  MODAL_SUBTITLE_OPTIONS_PERSONALIZE,
  CAROUSEL_MIN_IMAGES_WARNING,
  MODAL_CREATE_WITH_IA_LABEL_NUMBER_OF_IMAGES,
  MODAL_CREATE_WITH_IA_PLACEHOLDER_NUMBER_OF_IMAGES,
  SECTION_COUNTRY_DEFAULT_VALUE,
  LOGO_DESCRIPTION,
  MODAL_CREATE_WITH_IA_VIDEO_DURATION_20_SECONDS,
  MODAL_CREATE_WITH_IA_VIDEO_DURATION_30_SECONDS,
  MODAL_CREATE_WITH_IA_LABEL_VIDEO_DURATION_SECONDS,
  MODAL_CREATE_WITH_IA_REFERENCE_VIDEO_FORCES_CUSTOM,
} from "../../../i18n/keysTranslations";
import {
  COUNTRIES,
  MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_IMAGE,
  MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_VIDEO,
  PLAN_TYPE_PACKAGES,
} from "../../../utils/constants";
import { BUSINESS_LOOK_AND_FEEL_PLACEHOLDER } from "../../../i18n/keysTranslations";
import {
  BUTTON_UPLOAD_LOGO,
  BUTTON_CHANGE_LOGO,
} from "../../../i18n/keysTranslations";
import TextField from "../../Form/TextField";
import Select from "../../Form/Select";
import SelectContentLanguage from "../../Form/SelectContentLanguage";
import AutocompleteField from "../../Form/AutocompleteField";
import Button from "../../Buttons/Button";
import Switch from "../../Form/Switch";
import CardMedia from "../../Media/CardMedia";
import UploadFileButton from "../../Buttons/UploadFileButton";
import {
  BUSINESS_OFFERING_PHYSICAL_PRODUCT,
  BUSINESS_OFFERING_SERVICES,
  MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT,
  MODAL_CREATE_WITH_IA_MEDIA_TYPES,
  MODAL_CREATE_WITH_IA_PLATFORMS,
  LOGO_IMAGE_FORMATS,
  VIDEO_FORMATS,
  IMAGE_FORMATS,
} from "../../../utils/constants";
import { useState, useEffect, useCallback } from "react";
import ReferenceImageSelector from "./ReferenceImageSelector";
import ReferenceAdSelector from "./ReferenceAdSelector";
import {
  optionsVideoTypesForPhysicalProduct,
  optionsVideoTypesForServices,
  GENERIC_ACCENTS,
  getDefaultAccentForLanguage,
} from "../../../utils/options";
import { CONTENT_LANGUAGE_PRESET } from "../../../utils/catalogs/contentLanguage";
import ModalGalleryFromUrl, {
  MODAL_GALLERY_FETCH_MODE_URL,
} from "../ModalGalleryFromUrl";
import ModalCompetitors, {
  MODAL_COMPETITORS_FROM_CREATE_WITH_IA,
} from "../ModalCompetitors";
import ModalSubtitleOptions from "./ModalSubtitleOptions";
import AdditionalAssetsToggleSection from "./AdditionalAssetsToggleSection";
import AdGenerationTemplateField from "./AdGenerationTemplateField";
import { shouldLockHasTextForAdTemplate } from "../../../utils/adGenerationTemplates";
import { getExtensionFile } from "../../../utils/string";
import { useCompetitorScopeGuard } from "../../../hooks/useCompetitorScopeGuard";
import {
  ASPECT_RATIO_SECTION_ID,
  CONFIG_PANEL_SCROLL_CONTAINER_ID,
  REFERENCE_AD_SECTION_ID,
  scrollToConfigPanelSection,
} from "./utils/configPanelScroll";
import {
  getCreateWithIAReferenceImageLimit,
  getRemainingReferenceImageSlots,
  mergeReferenceImageUrls,
} from "../../../utils/createWithIAReferenceImages";

const ConfigurationPanel = ({
  control,
  errors,
  setValue,
  getValues,
  clearErrors,
  watch,
  isPreparingSubmit = false,
  pendingGenerationsCount = 0,
  onSubmit,
  onOpenGallery,
  onOpenGalleryForReferenceAd = () => {},
  isGalleryOpen,
  onToggleConfig,
  businessOffering,
  mediaType,
  logoUrl,
  userID,
  onSaveLogo,
  onChangeBrandInformation = () => {},
  defaultAdvancedAccordionExpanded = false,
  additionalAssets = [],
  additionalAssetsEnabledMap = {},
  onToggleAdditionalAsset = () => {},
  brandInformation = {},
  preserveHasLogo = false,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isUrlGalleryOpen, setIsUrlGalleryOpen] = useState(false);
  const [isMetaAdLibraryGalleryOpen, setIsMetaAdLibraryGalleryOpen] =
    useState(false);
  const [isSubtitleOptionsModalOpen, setIsSubtitleOptionsModalOpen] =
    useState(false);
  const [isAdvancedConfigOpen, setIsAdvancedConfigOpen] = useState(
    defaultAdvancedAccordionExpanded
  );
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [showLogo, setShowLogo] = useState(
    preserveHasLogo ? !!getValues("hasLogo") : true
  );
  const { t } = useTranslationApp();
  const { onTryOpenCompetitorScope } = useCompetitorScopeGuard({
    brandInformation,
  });

  const imagesUrl = watch("imagesUrl") || [];
  const maxReferenceImages = getCreateWithIAReferenceImageLimit({ mediaType });
  const remainingReferenceImageSlots = getRemainingReferenceImageSlots({
    currentCount: imagesUrl.length,
    mediaType,
  });

  const onOpenReferenceImagesFromUrl = useCallback(() => {
    const websiteUrl = getValues("websiteUrl")?.trim?.() || "";
    if (!websiteUrl || imagesUrl.length >= maxReferenceImages) {
      return;
    }
    setIsUrlGalleryOpen(true);
  }, [getValues, imagesUrl.length, maxReferenceImages]);

  const onOpenMetaAdLibraryGallery = useCallback(() => {
    onTryOpenCompetitorScope({
      onOpen: () => setIsMetaAdLibraryGalleryOpen(true),
    });
  }, [onTryOpenCompetitorScope]);

  // Get subscription info for creatives count
  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const subscriptionPlan = useSelectorApp(
    (state) => state.payments.subscriptionPlan
  );

  const { typeOfPlans } = useSelectorApp((state) => state.payments);
  const planType = typeOfPlans || PLAN_TYPE_PACKAGES;

  // Calculate remaining creatives
  const showRestCreatives =
    subscriptionPlan ||
    (!subscriptionPlan && subscriptionPlanInfo?.benefits?.creatives > 0);
  const showRestVideos =
    subscriptionPlan ||
    (!subscriptionPlan && subscriptionPlanInfo?.benefits?.videos > 0);

  const restCreatives = subscriptionPlanInfo?.usage?.creatives || 0;
  const restVideos = subscriptionPlanInfo?.usage?.videos || 0;
  const showRemainingMediaBalance =
    planType === PLAN_TYPE_PACKAGES &&
    (mediaType === "video" ? showRestVideos : showRestCreatives);

  const referenceAdUrl = watch("referenceAdUrl");
  const referenceAdMediaType = watch("referenceAdMediaType");
  const hasVideoReference =
    mediaType === "video" &&
    Boolean(referenceAdUrl) &&
    (referenceAdMediaType === "video" ||
      VIDEO_FORMATS.includes(getExtensionFile(referenceAdUrl)));
  const hasText = watch("hasText");
  const templateID = watch("templateID") || "";
  const isLockedStaticTemplate = shouldLockHasTextForAdTemplate({
    templateID,
    mediaType,
  });
  const hasSubtitles = watch("hasSubtitles");
  const hasWatermark = watch("hasWatermark");
  const prompt = watch("prompt");
  const selectedLanguage = watch("language");
  const numberOfImages = watch("numberOfImages");
  const contentLanguagePreset =
    mediaType === "video"
      ? CONTENT_LANGUAGE_PRESET.VIDEO
      : CONTENT_LANGUAGE_PRESET.IMAGE;

  const isVideoThemePrompt = mediaType === "video";
  const isReferenceAdMediaType =
    mediaType === "image" || mediaType === "carousel" || mediaType === "video";

  // Character limit applies to image/carousel instructions, not video theme.
  // Do not use RHF maxLength: it stays registered after switching image → video.
  const isPromptTooLong =
    !isVideoThemePrompt &&
    prompt &&
    prompt.length > MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT;

  const validatePromptLength = (value) => {
    const currentMediaType = getValues("mediaType");
    if (currentMediaType === "video") {
      return true;
    }
    if (
      value &&
      value.length > MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT
    ) {
      return t(FIELD_NUMBER_GREATER_THAN, {
        value: MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT,
      });
    }
    return true;
  };

  // Check if carousel images count exceeds maximum
  const isCarouselCountExceeded =
    mediaType === "carousel" && numberOfImages && Number(numberOfImages) > 10;

  const onNumberOfImagesKeyDown = (event) => {
    if (["ArrowUp", "ArrowDown", "-", "+"].includes(event.key)) {
      event.preventDefault();
    }
  };

  const onNumberOfImagesChange = (value) => {
    const numValue = Number(value);
    if (value !== "" && !Number.isNaN(numValue) && numValue < 0) {
      setValue("numberOfImages", "");
    }
  };

  // Function to get accent options based on the selected language
  const getAccentOptions = () => {
    if (!selectedLanguage || !GENERIC_ACCENTS[selectedLanguage]) {
      return [];
    }

    return GENERIC_ACCENTS[selectedLanguage].map((accent) => ({
      value: accent.value,
      label: accent.labelKey, // The Select will translate this labelKey when doTranslate=true
    }));
  };

  useEffect(() => {
    if (mediaType === "video") {
      setShowLogo(false);
      setValue("hasLogo", false);
      return;
    }

    if (preserveHasLogo) {
      setShowLogo(!!getValues("hasLogo"));
      return;
    }

    const hasLogoValue = !!logoUrl;
    setShowLogo(hasLogoValue);
    setValue("hasLogo", hasLogoValue);
  }, [logoUrl, mediaType, setValue, getValues, preserveHasLogo]);

  useEffect(() => {
    if (defaultAdvancedAccordionExpanded) {
      setIsAdvancedConfigOpen(true);
    }
  }, [defaultAdvancedAccordionExpanded]);

  // Set default accent when the selected language changes
  useEffect(() => {
    if (mediaType !== "video") return;

    const previousAccent = getValues("accent");
    const accentOptions = GENERIC_ACCENTS[selectedLanguage] || [];
    const isCurrentAccentValid = accentOptions.some(
      (option) => option.value === previousAccent
    );
    const nextAccent = accentOptions.length
      ? isCurrentAccentValid
        ? previousAccent
        : getDefaultAccentForLanguage(selectedLanguage)
      : null;

    if (previousAccent === nextAccent) return;
    setValue("accent", nextAccent);
  }, [selectedLanguage, mediaType, setValue, getValues]);

  // Set isCarousel when mediaType changes to/from carousel
  useEffect(() => {
    if (mediaType === "carousel") {
      setValue("isCarousel", true);
    } else if (mediaType !== "image") {
      setValue("isCarousel", false);
      setValue("numberOfImages", "");
    }
  }, [mediaType, setValue]);

  useEffect(() => {
    if (mediaType === "video") {
      clearErrors?.("prompt");
    }
  }, [mediaType, clearErrors]);

  useEffect(() => {
    const url = getValues("referenceAdUrl");
    if (!url) return;

    const extension = getExtensionFile(url);
    const isVideoFile = VIDEO_FORMATS.includes(extension);
    const isImageFile = IMAGE_FORMATS.includes(extension);
    const storedType = getValues("referenceAdMediaType");
    const isVideoReference =
      isVideoFile || (!isImageFile && storedType === "video");
    const wantsVideoReference = mediaType === "video";

    if (wantsVideoReference === isVideoReference) return;

    setValue("referenceAdUrl", "");
    setValue("referenceAdMediaType", "");
  }, [mediaType, getValues, setValue]);

  useEffect(() => {
    if (!hasVideoReference) return;
    if (getValues("videoType") === "other") return;
    setValue("videoType", "other");
  }, [hasVideoReference, getValues, setValue]);

  // Text-first plantillas are nearly empty with text off. Keep the copy on.
  useEffect(() => {
    if (!isLockedStaticTemplate || hasText) return;
    setValue("hasText", true);
  }, [isLockedStaticTemplate, hasText, setValue]);

  useEffect(() => {
    if (!logoUrl) return;

    const extension = getExtensionFile(logoUrl);
    if (!extension) return;

    if (!LOGO_IMAGE_FORMATS.includes(extension)) {
      setValue("hasLogo", false);
      onChangeBrandInformation((prev) => ({ ...prev, logoUrl: "" }));
    }

    //eslint-disable-next-line
  }, []);

  return (
    <Box
      id={CONFIG_PANEL_SCROLL_CONTAINER_ID}
      sx={{
        width: { xs: "100%", sm: 350 },
        maxWidth: { xs: "100%", sm: 350 },
        bgcolor: "background.paper",
        borderRight: { xs: 0, sm: "1px solid rgba(0, 0, 0, 0.12)" },
        px: { xs: 2, sm: 3 },
        pt: { xs: 2, sm: 3 },
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        position: "relative",
        height: "100%",
        minWidth: 0, // Allow shrinking below content size
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          position: "relative",
          pr: isSmallScreen ? 5 : 0, // Add padding right when hamburger is visible
        }}
      >
        <Box
          sx={{
            p: 1.5,
            bgcolor: "primary.main",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SparklesIcon sx={{ color: "#ffffff !important", fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {" "}
          {/* Allow text to shrink */}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {t(MODAL_CREATE_WITH_IA_TITLE)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {t(MODAL_CREATE_WITH_IA_SUBTITLE)}
          </Typography>
        </Box>
        {isSmallScreen && (
          <IconButton
            onClick={onToggleConfig}
            size="small"
            sx={{
              position: "absolute",
              top: "45%",
              right: 0,
              transform: "translateY(-50%)",
              zIndex: 3,
              "&:hover": {
                bgcolor: "transparent",
              },
            }}
            aria-label="Cerrar configuración"
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* Basic Configuration */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SettingsIcon sx={{ fontSize: 19, color: "text.secondary" }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {t(MODAL_CREATE_WITH_IA_CONFIGURATION_BASIC)}
          </Typography>
        </Box>

        <Select
          name="mediaType"
          control={control}
          variant="filled"
          options={MODAL_CREATE_WITH_IA_MEDIA_TYPES}
          label={t(MODAL_CREATE_WITH_IA_LABEL_MEDIA_TYPE)}
          fullWidth
          size="small"
          doTranslate={true}
          errors={errors}
        />

        <Box id={ASPECT_RATIO_SECTION_ID}>
          <Select
            name="aspectRatio"
            control={control}
            variant="filled"
            options={
              mediaType === "video"
                ? MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_VIDEO
                : MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_IMAGE
            }
            label={t(ASPECT_RATIO_PLACEHOLDER)}
            fullWidth
            size="small"
            doTranslate={true}
            errors={errors}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
              validate: (value) => {
                const options =
                  mediaType === "video"
                    ? MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_VIDEO
                    : MODAL_CREATE_WITH_IA_ASPECT_RATIO_OPTIONS_IMAGE;
                if (
                  !value ||
                  !options.some((option) => option.value === value)
                ) {
                  return t(FIELD_REQUIRED);
                }
                return true;
              },
            }}
          />
        </Box>

        {mediaType === "video" && (
          <>
            <Select
              name="videoType"
              control={control}
              variant="filled"
              disabled={hasVideoReference}
              options={
                businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT
                  ? optionsVideoTypesForPhysicalProduct
                  : optionsVideoTypesForServices
              }
              label={t(MODAL_CREATE_WITH_IA_LABEL_VIDEO_TYPE)}
              fullWidth
              size="small"
              doTranslate={true}
              errors={errors}
            />
            {hasVideoReference ? (
              <Typography variant="caption" sx={{ color: "grey.600", mt: -1 }}>
                {t(MODAL_CREATE_WITH_IA_REFERENCE_VIDEO_FORCES_CUSTOM)}
              </Typography>
            ) : null}
            <Select
              name="duration"
              control={control}
              variant="filled"
              options={[
                {
                  value: 20,
                  label: MODAL_CREATE_WITH_IA_VIDEO_DURATION_20_SECONDS,
                },
                {
                  value: 30,
                  label: MODAL_CREATE_WITH_IA_VIDEO_DURATION_30_SECONDS,
                },
              ]}
              label={t(MODAL_CREATE_WITH_IA_LABEL_VIDEO_DURATION_SECONDS)}
              fullWidth
              size="small"
              doTranslate={true}
              errors={errors}
            />
          </>
        )}

        {(mediaType === "image" || mediaType === "carousel") && (
          <>
            <Select
              name="platform"
              control={control}
              variant="filled"
              options={MODAL_CREATE_WITH_IA_PLATFORMS}
              label={t(MODAL_CREATE_WITH_IA_LABEL_PLATFORM)}
              fullWidth
              size="small"
              doTranslate={true}
              errors={errors}
              rules={{ required: false }}
            />

            {/* Number of images input - show when mediaType is image or carousel */}
            {(mediaType === "image" || mediaType === "carousel") && (
              <TextField
                control={control}
                name="numberOfImages"
                variant="filled"
                label={t(MODAL_CREATE_WITH_IA_LABEL_NUMBER_OF_IMAGES)}
                placeholder={t(
                  MODAL_CREATE_WITH_IA_PLACEHOLDER_NUMBER_OF_IMAGES
                )}
                fullWidth
                size="small"
                type="number"
                errors={errors}
                onKeyDown={onNumberOfImagesKeyDown}
                actionsChange={onNumberOfImagesChange}
                inputProps={{
                  min: mediaType === "carousel" ? 2 : 1,
                  max: 10,
                }}
                rules={{
                  required: {
                    value: true,
                    message: t(FIELD_REQUIRED),
                  },
                  min: {
                    value: mediaType === "carousel" ? 2 : 1,
                    message:
                      mediaType === "carousel"
                        ? t(CAROUSEL_MIN_IMAGES_WARNING)
                        : t(FIELD_NUMBER_0_OR_NEGATIVE),
                  },
                  max: {
                    value: 10,
                    message: t(FIELD_NUMBER_GREATER_THAN, { value: 10 }),
                  },
                  validate: (value) => {
                    const numValue = Number(value);
                    const minValue = mediaType === "carousel" ? 2 : 1;
                    if (
                      isNaN(numValue) ||
                      numValue < minValue ||
                      !Number.isInteger(numValue)
                    ) {
                      return mediaType === "carousel"
                        ? t(CAROUSEL_MIN_IMAGES_WARNING)
                        : t(FIELD_NUMBER_0_OR_NEGATIVE);
                    }
                    if (numValue > 10) {
                      return t(FIELD_NUMBER_GREATER_THAN, { value: 10 });
                    }
                    return true;
                  },
                }}
              />
            )}
          </>
        )}

        {/* Text in image toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {mediaType === "video" ? t(VIDEO_LANGUAGE) : t(TEXT_IN_IMAGE)}
          </Typography>
          {(mediaType === "image" || mediaType === "carousel") && (
            <Switch
              id="has-text-switch"
              isActive={!!hasText}
              size="small"
              hasLabel={false}
              disabled={isLockedStaticTemplate}
              onChangeIsActive={(checked) => {
                if (isLockedStaticTemplate) return;
                setValue("hasText", checked);
              }}
            />
          )}
        </Box>

        {hasText && (
          <SelectContentLanguage
            name="language"
            control={control}
            preset={contentLanguagePreset}
            persistOnChange
            variant="filled"
            label={t(MODAL_CREATE_WITH_IA_LABEL_LANGUAGE)}
            fullWidth
            size="small"
            disableClearable={true}
            errors={errors}
          />
        )}

        {mediaType === "video" &&
          GENERIC_ACCENTS[selectedLanguage] &&
          getAccentOptions().length > 0 && (
            <Select
              name="accent"
              control={control}
              variant="filled"
              options={getAccentOptions()}
              label={t(MODAL_CREATE_WITH_IA_LABEL_ACCENT)}
              fullWidth
              size="small"
              doTranslate={true}
              errors={errors}
            />
          )}

        {mediaType === "video" && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {t(MODAL_CREATE_WITH_IA_LABEL_WITH_SUBTITLES)}
              </Typography>
              <Switch
                id="has-subtitles-switch"
                isActive={!!hasSubtitles}
                size="small"
                hasLabel={false}
                onChangeIsActive={(checked) =>
                  setValue("hasSubtitles", checked)
                }
              />
            </Box>
            {hasSubtitles && (
              <Typography
                component="span"
                onClick={() => setIsSubtitleOptionsModalOpen(true)}
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "primary.main",
                  mt: 0.5,
                  display: "inline-block",
                  "&:hover": { color: "primary.dark" },
                }}
              >
                {t(MODAL_SUBTITLE_OPTIONS_PERSONALIZE)}
              </Typography>
            )}
          </Box>
        )}

        {mediaType === "video" && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {t(MODAL_CREATE_WITH_IA_LABEL_AI_WATERMARK)}
              </Typography>
              <Switch
                id="has-watermark-switch"
                isActive={!!hasWatermark}
                size="small"
                hasLabel={false}
                onChangeIsActive={(checked) =>
                  setValue("hasWatermark", checked)
                }
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: 12,
                lineHeight: 1.6,
                mt: 0.5,
              }}
            >
              {t(MODAL_CREATE_WITH_IA_AI_WATERMARK_DESCRIPTION)}
            </Typography>
          </Box>
        )}

        {(mediaType === "image" || mediaType === "carousel") && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                {t(LOGO_IN_IMAGE)}
              </Typography>
              <Switch
                id="has-logo-switch"
                isActive={showLogo}
                size="small"
                hasLabel={false}
                onChangeIsActive={(checked) => {
                  setShowLogo(checked);
                  setValue("hasLogo", checked);
                }}
              />
            </Box>

            {showLogo && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.6 }}
              >
                {t(LOGO_DESCRIPTION)}
              </Typography>
            )}

            {showLogo && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 1,
                  mb: 1,
                }}
              >
                {logoUrl && (
                  <CardMedia
                    url={logoUrl}
                    sx={{
                      objectFit: "cover",
                      borderRadius: 3,
                      height: 120,
                      width: 120,
                      mb: 1,
                    }}
                    sxImage={{
                      border: "1px solid rgba(0, 0, 0, 0.12)",
                    }}
                  />
                )}
                <UploadFileButton
                  id="upload-logo-url"
                  textUpload={BUTTON_UPLOAD_LOGO}
                  colorUpload="secondary"
                  textRemove={BUTTON_CHANGE_LOGO}
                  colorRemove="secondary"
                  disableVerificationMedia={true}
                  value={logoUrl}
                  onChange={async (value) => {
                    const url = value?.[0];
                    if (onSaveLogo) {
                      await onSaveLogo(url);
                    }
                  }}
                  pathStorage={`socialMedia/${userID}/`}
                  acceptedFormats={LOGO_IMAGE_FORMATS}
                  modeUpload="sync"
                  loading={isUploadingLogo}
                  onCallbackLoading={(loading) => setIsUploadingLogo(loading)}
                  sx={{
                    fontSize: 13,
                    height: 40,
                    width: 110,
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Show here only for physical products */}
      {businessOffering === BUSINESS_OFFERING_PHYSICAL_PRODUCT && (
        <Box id="create-with-ia-reference-images-section">
          <ReferenceImageSelector
            control={control}
            errors={errors}
            onExtractFromUrl={onOpenReferenceImagesFromUrl}
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            onOpenGallery={onOpenGallery}
            businessOffering={businessOffering}
            mediaType={mediaType}
          />
        </Box>
      )}

      {/* Advanced Configuration */}
      <Accordion
        expanded={isAdvancedConfigOpen}
        onChange={() => setIsAdvancedConfigOpen(!isAdvancedConfigOpen)}
        sx={{
          boxShadow: "none",
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            margin: 0,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            p: 0,
            minHeight: 40,
            "&.Mui-expanded": {
              minHeight: 40,
            },
            "& .MuiAccordionSummary-content": {
              margin: "8px 0",
              "&.Mui-expanded": {
                margin: "8px 0",
              },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SettingsIcon sx={{ fontSize: 19, color: "text.secondary" }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t(MODAL_CREATE_WITH_IA_CONFIGURATION_ADVANCED)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: "0 0 16px 0",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {mediaType !== "video" && (
            <AdGenerationTemplateField
              control={control}
              errors={errors}
              setValue={setValue}
              watch={watch}
              mediaType={mediaType}
            />
          )}
          {businessOffering === BUSINESS_OFFERING_SERVICES ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {isReferenceAdMediaType && (
                <Box id={REFERENCE_AD_SECTION_ID}>
                  <ReferenceAdSelector
                    setValue={setValue}
                    watch={watch}
                    onOpenGallery={onOpenGalleryForReferenceAd}
                    onImportFromMetaAdLibrary={onOpenMetaAdLibraryGallery}
                    acceptVideo={mediaType === "video"}
                  />
                </Box>
              )}
              <ReferenceImageSelector
                control={control}
                errors={errors}
                onExtractFromUrl={onOpenReferenceImagesFromUrl}
                setValue={setValue}
                getValues={getValues}
                watch={watch}
                onOpenGallery={onOpenGallery}
                businessOffering={businessOffering}
                mediaType={mediaType}
              />
            </Box>
          ) : (
            isReferenceAdMediaType && (
              <Box id={REFERENCE_AD_SECTION_ID}>
                <ReferenceAdSelector
                  setValue={setValue}
                  watch={watch}
                  onOpenGallery={onOpenGalleryForReferenceAd}
                  onImportFromMetaAdLibrary={onOpenMetaAdLibraryGallery}
                  acceptVideo={mediaType === "video"}
                />
              </Box>
            )
          )}
          {/* Localización - solo para imágenes y carrusel */}
          {(mediaType === "image" || mediaType === "carousel") && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {t(MODAL_CREATE_WITH_IA_LABEL_LOCATION)}
              </Typography>
              <AutocompleteField
                control={control}
                name="imageLocation"
                options={COUNTRIES}
                doTranslate
                fullWidth
                variant="filled"
                placeholder={t(SECTION_COUNTRY_DEFAULT_VALUE)}
                errors={errors}
                rules={{ required: false }}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t(
                isVideoThemePrompt
                  ? MODAL_CREATE_WITH_IA_CONFIGURATION_THEME
                  : MODAL_CREATE_WITH_IA_CONFIGURATION_INSTRUCTIONS
              )}
            </Typography>
            <TextField
              control={control}
              name="prompt"
              multiline
              minRows={4}
              maxRows={6}
              placeholder={t(
                isVideoThemePrompt
                  ? MODAL_CREATE_WITH_IA_PROMPT_THEME_OPTIONAL
                  : MODAL_CREATE_WITH_IA_PROMPT_OPTIONAL
              )}
              fullWidth
              variant="filled"
              errors={errors}
              showCounterCharacters={!isVideoThemePrompt}
              maxCharacters={
                isVideoThemePrompt
                  ? undefined
                  : MODAL_CREATE_WITH_IA_MAX_CHARACTERS_DESCRIPTION_PROMPT
              }
              rules={{
                required: false,
                validate: validatePromptLength,
              }}
            />
          </Box>

          {/* Business Look & Feel field below description */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t(BUSINESS_LOOK_AND_FEEL_PLACEHOLDER)}
            </Typography>
            <TextField
              control={control}
              name="businessLookAndFeel"
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              variant="filled"
              errors={errors}
              rules={{ required: false }}
            />
          </Box>

          {mediaType !== "video" &&
            brandInformation?.hasAdditionalAssets === true && (
              <AdditionalAssetsToggleSection
                assets={additionalAssets}
                enabledMap={additionalAssetsEnabledMap}
                onToggleAsset={onToggleAdditionalAsset}
              />
            )}
        </AccordionDetails>
      </Accordion>

      {/* Generate Button - Fixed at the bottom */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          bgcolor: "background.paper",
          pt: 2,
          pb: 2,
          mt: "auto",
          zIndex: 6,
        }}
      >
        {/* Creatives remaining message */}
        {showRemainingMediaBalance && (
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "text.secondary",
              mb: 1,
              fontSize: { xs: 13, sm: 13 },
              opacity: 0.8,
            }}
          >
            {t(
              mediaType === "video"
                ? restVideos === 1
                  ? VIDEOS_REMAINING_SHORT_SINGULAR
                  : VIDEOS_REMAINING_SHORT
                : restCreatives === 1
                  ? CREATIVES_REMAINING_SHORT_SINGULAR
                  : CREATIVES_REMAINING_SHORT,
              {
                value:
                  mediaType === "video"
                    ? restVideos > 0
                      ? restVideos
                      : 0
                    : restCreatives > 0
                      ? restCreatives
                      : 0,
              }
            )}
          </Typography>
        )}
        <Button
          color="primary"
          onClick={() => {
            onSubmit();
            if (isSmallScreen && onToggleConfig) {
              onToggleConfig();
            }
          }}
          disabled={isGalleryOpen || isPromptTooLong || isCarouselCountExceeded}
          variant="contained"
          fullWidth
          size="large"
          startIcon={
            isPreparingSubmit ? (
              <CircularProgress size={{ xs: 16, sm: 20 }} color="inherit" />
            ) : (
              <SparklesIcon
                sx={{
                  color: "#ffffff !important",
                  fontSize: { xs: 12, sm: 16 },
                }}
              />
            )
          }
          sx={{
            height: { xs: 44, sm: 48 },
          }}
        >
          {t(MODAL_CREATE_WITH_IA_GENERATE_WITH_AI)}
        </Button>
        {pendingGenerationsCount > 0 ? (
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "text.secondary",
              mt: 1,
              fontSize: { xs: 13, sm: 13 },
            }}
          >
            {t(MODAL_CREATE_WITH_IA_GENERATIONS_IN_PROGRESS, {
              count: pendingGenerationsCount,
            })}
          </Typography>
        ) : null}
      </Box>

      {/* Modal: Load images from website URL (reference images) */}
      {isUrlGalleryOpen && remainingReferenceImageSlots > 0 && (
        <ModalGalleryFromUrl
          modalOpen={isUrlGalleryOpen}
          onCloseModal={() => setIsUrlGalleryOpen(false)}
          fetchMode={MODAL_GALLERY_FETCH_MODE_URL}
          fetchUrl={(getValues("websiteUrl") || "").trim()}
          multiple={true}
          maxSelections={remainingReferenceImageSlots}
          onCallbackSelectedItems={(items) => {
            const current = getValues("imagesUrl") || [];
            const urls = items.map((it) => it.url).filter(Boolean);
            setValue(
              "imagesUrl",
              mergeReferenceImageUrls({
                currentUrls: current,
                newUrls: urls,
                mediaType,
              })
            );
          }}
        />
      )}

      {/* Modal: Meta Ad Library (reference ad, base64 only) */}
      {isMetaAdLibraryGalleryOpen && (
        <ModalCompetitors
          modalOpen={isMetaAdLibraryGalleryOpen}
          onCloseModal={() => setIsMetaAdLibraryGalleryOpen(false)}
          from={MODAL_COMPETITORS_FROM_CREATE_WITH_IA}
          acceptedReferenceMediaType={mediaType === "video" ? "video" : "image"}
          onCallbackSelectedItems={(items) => {
            const selectedUrl = items?.[0]?.url;
            if (!selectedUrl) return;

            setIsMetaAdLibraryGalleryOpen(false);
            setValue("referenceAdUrl", selectedUrl);
            setValue(
              "referenceAdMediaType",
              items?.[0]?.type === "video" ? "video" : "image"
            );
            setIsAdvancedConfigOpen(true);
            scrollToConfigPanelSection({
              sectionId: REFERENCE_AD_SECTION_ID,
            });
          }}
        />
      )}

      {/* Modal: Personalizar opciones de subtítulos */}
      <ModalSubtitleOptions
        modalOpen={isSubtitleOptionsModalOpen}
        onCloseModal={() => setIsSubtitleOptionsModalOpen(false)}
        initialValues={getValues("subtitleOptions")}
        onSave={(options) => {
          setValue("subtitleOptions", options);
        }}
      />
    </Box>
  );
};

export default ConfigurationPanel;
